import { useEffect, useState } from 'react';
import {
  IconUp,
  IconDown,
  Checkbox,
  notificationCategoriesTree,
  InputText,
  InputSize,
} from '@ui/src';
import BacklightText from './backlight-text';
import {
  CategoryFilterCategoryTypes,
  CategoryFilterGroupTypes,
  CategoryFilterTypeTypes,
  SidepanelCategoryFilterTypes,
} from './types';

import cn from './styles.module.css';

const Type = ({
  type,
  setValue,
  filterFields,
  category,
  group,
  searchValue,
  withoutCategories,
}: CategoryFilterTypeTypes) => {
  const nameArray = type.name.split(' ');

  return (
    <div className={cn.sidepanelFilterCategory}>
      <Checkbox
        title={
          <BacklightText
            searchQuery={searchValue || ''}
            text={`${nameArray[0]?.replace('Issues', 'Tasks')} - ${nameArray
              .slice(1)
              .join(' ')}`}
          />
        }
        value={type.value}
        type="checkbox"
        name={`category-${type.value}`}
        labelClassName={cn.filterCheckbox}
        checked={Boolean(
          filterFields?.type?.find(
            (action) => action?.replace('_ACTION', '') === type.value,
          ),
        )}
        onChange={() => {
          const nextSelectedCategories = ([] as string[]).concat(
            filterFields?.category as string[],
          );

          const nextSelectedGroups = ([] as string[]).concat(
            filterFields?.group as string[],
          );

          let nextSelectedActions = ([] as string[]).concat(
            filterFields?.type as string[],
          );

          if (nextSelectedActions?.find((action) => action === type.value)) {
            nextSelectedActions = nextSelectedActions?.filter(
              (action) => action !== type.value,
            );
          } else {
            nextSelectedActions.push(
              type.value?.replace('_ACTION', '') as string,
            );

            if (
              !filterFields?.category?.find(
                (categoryValue) => categoryValue === category,
              )
            ) {
              nextSelectedCategories.push(category as string);
            }

            if (
              !nextSelectedGroups?.find((groupValue) => groupValue === group)
            ) {
              nextSelectedGroups.push(group as string);
            }
          }

          setValue('type', nextSelectedActions);
          setValue('group', nextSelectedGroups);

          if (!withoutCategories) {
            setValue('category', nextSelectedCategories);
          }
        }}
      />
    </div>
  );
};

const Group = ({
  group,
  setValue,
  filterFields,
  category,
  categories,
  searchValue,
  withoutCategories,
}: CategoryFilterGroupTypes) => {
  const [collapsed, setCollapsed] = useState(false);

  const currentGroupSelectedActions: string[] = [];

  const currentCategory = categories?.find(
    (categoryItem: any) => categoryItem.page === category,
  );

  const currentGroup = currentCategory?.groups?.find(
    (groupItem: any) => groupItem.group === group.value,
  );

  if (currentGroup?.types) {
    filterFields?.type?.forEach((action) => {
      currentGroup?.types?.forEach((actionItem: any) => {
        if (
          actionItem.action === action &&
          !currentGroupSelectedActions.find(
            (selectedItem) => selectedItem === action,
          )
        ) {
          currentGroupSelectedActions.push(action);
        }
      });
    });
  }

  const types = currentGroup?.types?.map((item: any) => ({
    name: item.action
      ?.replace('_ACTION', '')
      ?.split('_')
      ?.map(
        (action: string) =>
          `${action.charAt(0)}${action.slice(1).toLowerCase()}`,
      )
      .join(' '),
    value: item.action?.replace('_ACTION', ''),
  }));

  return (
    <>
      <div className={cn.sidepanelFilterCategory}>
        <Checkbox
          title={group.name}
          value={group.value}
          type="checkbox"
          name={`category-${group.value}`}
          labelClassName={cn.filterCheckbox}
          checked={Boolean(
            filterFields?.group?.find(
              (groupValue) => groupValue === group.value,
            ),
          )}
          partialChecked={Boolean(
            currentGroupSelectedActions.length &&
            currentGroupSelectedActions.length !== currentGroup?.types?.length,
          )}
          onChange={(e) => {
            const currentGroupActions = currentGroup?.types;

            let nextSelectedGroups = ([] as string[]).concat(
              filterFields?.group as string[],
            );
            let nextSelectedActions = ([] as string[]).concat(
              filterFields?.type as string[],
            );

            const nextSelectedCategories = ([] as string[]).concat(
              filterFields?.category as string[],
            );

            if (
              nextSelectedGroups?.find(
                (groupValue) => groupValue === group.value,
              )
            ) {
              nextSelectedGroups = nextSelectedGroups?.filter(
                (groupValue) => groupValue !== group.value,
              );
            } else {
              nextSelectedGroups.push(group.value as string);

              if (
                !nextSelectedCategories.find(
                  (categoryValue) => categoryValue === category,
                )
              ) {
                nextSelectedCategories.push(category as string);
              }
            }

            if (currentGroupActions) {
              if (
                e.target.checked ||
                (!filterFields?.group?.find(
                  (groupValue) => groupValue === group.value,
                ) &&
                  Boolean(
                    currentGroupSelectedActions.length &&
                    currentGroupSelectedActions.length !==
                      currentGroup.types?.length,
                  ))
              ) {
                currentGroupActions.forEach((actionItem: any) => {
                  if (
                    !nextSelectedActions.find(
                      (currentGroupActionKey) =>
                        currentGroupActionKey === actionItem.action,
                    )
                  ) {
                    nextSelectedActions.push(
                      actionItem.action?.replace('_ACTION', ''),
                    );
                  }
                });
              } else {
                nextSelectedActions = nextSelectedActions.filter((actionKey) =>
                  currentGroupActions.every(
                    (currentGroupActionItem: any) =>
                      actionKey !==
                      currentGroupActionItem.action?.replace('_ACTION', ''),
                  ),
                );
              }

              setValue('type', nextSelectedActions);
            }

            setValue('group', nextSelectedGroups);

            if (!withoutCategories) {
              setValue('category', nextSelectedCategories);
            }
          }}
        />
        {types && types?.length > 0 && (
          <button
            type="button"
            className={cn.collapseButton}
            onClick={() => setCollapsed(!collapsed)}
          >
            {collapsed ? <IconUp /> : <IconDown />}
          </button>
        )}
      </div>
      {collapsed && (
        <div className={cn.sidepanelTypesWrapper}>
          <div className={cn.line} />
          {types?.map((item: any) => (
            <Type
              key={item.value}
              type={item}
              category={category}
              group={currentGroup?.group}
              filterFields={filterFields}
              setValue={setValue}
              searchValue={searchValue}
              withoutCategories={withoutCategories}
            />
          ))}
        </div>
      )}
    </>
  );
};

const Category = ({
  category,
  setValue,
  filterFields,
  categories,
  searchValue,
  withoutCategories,
}: CategoryFilterCategoryTypes) => {
  const [collapsed, setCollapsed] = useState(false);

  const currentCategorySelectedActions: string[] = [];

  const currentCategory = categories?.find(
    (categoryItem: any) => categoryItem.page === category.value,
  );

  if (currentCategory?.groups) {
    filterFields?.group?.forEach((group) => {
      currentCategory?.groups?.forEach((groupItem: any) => {
        if (
          groupItem.group === group &&
          !currentCategorySelectedActions.find(
            (selectedItem) => selectedItem === group,
          )
        ) {
          currentCategorySelectedActions.push(group);
        }
      });
    });
  }

  const groups = currentCategory?.groups?.map((item: any) => ({
    name: item.group
      ?.split('_')
      ?.map((group: any) => `${group.charAt(0)}${group.slice(1).toLowerCase()}`)
      .join(' ')
      ?.replace('Issues', 'Tasks'),
    value: item.group,
  }));

  useEffect(() => {
    if (withoutCategories) {
      setCollapsed(true);
    }
  }, [withoutCategories]);

  return (
    <>
      {!withoutCategories && (
        <div className={cn.sidepanelFilterCategory}>
          <Checkbox
            title={category.name?.replace('Issues', 'Tasks')}
            value={category.value}
            type="checkbox"
            name={`category-${category.value}`}
            labelClassName={cn.filterCheckbox}
            partialChecked={Boolean(
              currentCategorySelectedActions.length &&
              currentCategorySelectedActions.length !==
                currentCategory?.groups?.length,
            )}
            checked={Boolean(
              filterFields?.category?.find(
                (categoryValue) => categoryValue === category.value,
              ),
            )}
            onChange={(e) => {
              const currentGroups = notificationCategoriesTree?.find(
                (categoryItem) => categoryItem.page === category.value,
              )?.groups;

              let nextSelectedCategories = ([] as string[]).concat(
                filterFields?.category as string[],
              );
              let nextSelectedGroups = ([] as string[]).concat(
                filterFields?.group as string[],
              );
              let nextSelectedActions = ([] as string[]).concat(
                filterFields?.type as string[],
              );

              if (
                nextSelectedCategories?.find(
                  (categoryValue) => categoryValue === category.value,
                )
              ) {
                nextSelectedCategories = nextSelectedCategories?.filter(
                  (categoryValue) => categoryValue !== category.value,
                );
              } else {
                nextSelectedCategories.push(category.value as string);
              }

              if (currentGroups) {
                if (
                  e.target.checked ||
                  (!filterFields?.category?.find(
                    (categoryValue) => categoryValue === category.value,
                  ) &&
                    Boolean(
                      currentCategorySelectedActions.length &&
                      currentCategorySelectedActions.length !==
                        notificationCategoriesTree[category.value]?.groups
                          ?.length,
                    ))
                ) {
                  currentGroups.forEach((groupItem) => {
                    if (
                      !nextSelectedGroups.find(
                        (currentGroupKey) =>
                          currentGroupKey === groupItem.group,
                      )
                    ) {
                      nextSelectedGroups.push(groupItem.group);
                    }

                    groupItem?.types?.forEach((actionItem) => {
                      if (
                        !nextSelectedActions.find(
                          (currentGroupActionKey) =>
                            currentGroupActionKey ===
                            actionItem.action?.replace('_ACTION', ''),
                        )
                      ) {
                        nextSelectedActions.push(
                          actionItem.action?.replace('_ACTION', ''),
                        );
                      }
                    });
                  });
                } else {
                  nextSelectedGroups = nextSelectedGroups.filter((groupKey) =>
                    currentGroups.every(
                      (currentGroupItem) => groupKey !== currentGroupItem.group,
                    ),
                  );

                  const currentSelectedGroups =
                    notificationCategoriesTree?.find(
                      (categoryItem) => categoryItem.page === e.target.value,
                    )?.groups;

                  if (currentSelectedGroups) {
                    const actions: string[] = [];

                    currentSelectedGroups?.forEach((groupItem) => {
                      groupItem?.types?.forEach((actionItem) => {
                        actions.push(actionItem.action);
                      });
                    });

                    nextSelectedActions = nextSelectedActions.filter(
                      (actionKey) =>
                        actions.every(
                          (action) =>
                            actionKey !== action?.replace('_ACTION', ''),
                        ),
                    );
                  }
                }

                setValue('type', nextSelectedActions);
                setValue('group', nextSelectedGroups);
              }
              setValue('category', nextSelectedCategories);
            }}
          />
          {groups && groups?.length > 0 && (
            <button
              type="button"
              className={cn.collapseButton}
              onClick={() => setCollapsed(!collapsed)}
            >
              {collapsed ? <IconUp /> : <IconDown />}
            </button>
          )}
        </div>
      )}
      {collapsed && (
        <div
          className={`${cn.sidepanelGroupsWrapper} ${
            withoutCategories ? cn.withoutPadding : ''
          }`}
        >
          {!withoutCategories && <div className={cn.line} />}
          <div className={cn.groupsWrapper}>
            {groups?.map((item: any) => (
              <Group
                key={item.value}
                group={item}
                setValue={setValue}
                category={currentCategory?.page}
                filterFields={filterFields}
                categories={categories}
                searchValue={searchValue}
                withoutCategories={withoutCategories}
              />
            ))}
          </div>
        </div>
      )}
    </>
  );
};

const SidepanelCategoryFilter = ({
  setValue,
  filterFields,
  withoutCategories,
  category: categoryValue,
}: SidepanelCategoryFilterTypes) => {
  const [searchValue, setSearchValue] = useState('');
  const [categoriesList, setCategoriesList] = useState([]);
  const [categoriesItems, setCategoriesItems] = useState(
    notificationCategoriesTree,
  );

  const categories = notificationCategoriesTree
    ?.filter((item) => !item.hidden)
    ?.map((item) => ({
      name: item.page
        ?.split('_')
        .map((page) => `${page.charAt(0)}${page.slice(1).toLowerCase()}`)
        .join(' ')
        ?.replace('Issues', 'Tasks'),
      value: item.page,
    }));

  const onChange = (text: string) => {
    setSearchValue(text);

    if (text) {
      const filteredCategories: any = [];

      let categoryTree = notificationCategoriesTree?.filter(
        (item) => !item.hidden,
      );

      if (withoutCategories) {
        categoryTree = notificationCategoriesTree?.filter(
          (item) => item.page === categoryValue,
        );
      }

      categoryTree?.forEach((category) => {
        const filteredGroups: any = [];

        category?.groups?.forEach((group) => {
          const types = group?.types?.filter((type) => {
            const typeAction = type.action.replace('ISSUES', 'TASKS');

            return typeAction.toLowerCase().includes(text.toLowerCase());
          });

          if (types?.length) {
            filteredGroups.push({
              ...group,
              types,
            });
          }
        });

        if (filteredGroups?.length) {
          filteredCategories.push({
            ...category,
            groups: filteredGroups,
          });
        }
      });

      if (filteredCategories?.length) {
        setCategoriesItems(filteredCategories);
        setCategoriesList(
          filteredCategories?.map((item: any) => ({
            name: item.page
              ?.split('_')
              .map(
                (page: any) =>
                  `${page.charAt(0)}${page.slice(1).toLowerCase()}`,
              )
              .join(' ')
              ?.replace('Issues', 'Tasks'),
            value: item.page,
          })),
        );
      } else {
        setCategoriesItems([]);
        setCategoriesList([]);
      }
    } else {
      // eslint-disable-next-line no-lonely-if
      if (!withoutCategories) {
        setCategoriesItems(notificationCategoriesTree);
        setCategoriesList(categories as any);
      } else {
        const currentCategories = notificationCategoriesTree
          ?.filter((item) => item.page === categoryValue)
          ?.map((item) => ({
            name: item.page
              ?.split('_')
              .map((page) => `${page.charAt(0)}${page.slice(1).toLowerCase()}`)
              .join(' ')
              ?.replace('Issues', 'Tasks'),
            value: item.page,
          }));

        setCategoriesItems(
          notificationCategoriesTree?.filter(
            (item) => item.page === categoryValue,
          ),
        );
        setCategoriesList(currentCategories as any);
      }
    }
  };

  useEffect(() => {
    if (!withoutCategories) {
      setCategoriesList(categories as any);
      setCategoriesItems(
        notificationCategoriesTree?.filter((item) => !item.hidden),
      );
    } else {
      const currentCategories = notificationCategoriesTree
        ?.filter((item) => item.page === categoryValue)
        ?.map((item) => ({
          name: item.page
            ?.split('_')
            .map((page) => `${page.charAt(0)}${page.slice(1).toLowerCase()}`)
            .join(' ')
            ?.replace('Issues', 'Tasks'),
          value: item.page,
        }));

      setCategoriesList(currentCategories as any);
      setCategoriesItems(
        notificationCategoriesTree?.filter(
          (item) => item.page === categoryValue,
        ),
      );
    }
  }, [withoutCategories]);

  return (
    <div className={cn.sidepanelCategoryFilter}>
      <div className={cn.sidepanelCategoryFilterTitle}>
        Category, Group & Types of Entity
      </div>
      {(categoriesItems?.filter((item) =>
        item.groups?.find((group) => group.types?.length),
      )?.length > 0 ||
        searchValue) && (
        <InputText
          name="searchValue"
          placeholder="Search by entity"
          value={searchValue}
          onChange={(e) => onChange(e.target.value)}
          size={InputSize.SMALL}
          labelClassName={cn.searchInput}
        />
      )}
      {categoriesList?.map((item: any) => (
        <Category
          key={item.value}
          category={item}
          setValue={setValue}
          filterFields={filterFields}
          categories={categoriesItems}
          searchValue={searchValue}
          withoutCategories={withoutCategories}
        />
      ))}
      {!categoriesList?.length && (
        <div className={cn.emptyCategories}>No Entities found</div>
      )}
    </div>
  );
};

export default SidepanelCategoryFilter;
